/**
 * 列表入口"评论"列展示用的批量摘要 hook。
 *
 * 给一组 sceneIds + 可选 jobId，返回 ``{sceneId: {total, latestTitle}}``。
 * useEffect 内 dedup + 按 jobId 切换重拉；refresh() 给"评论后刷新"用。
 */
import { useCallback, useEffect, useState } from 'react';

import {
  getSceneCommentSummaryBatch,
  type SceneCommentSummary,
} from '@/apis/simulation';

export function useSceneCommentSummary(
  sceneIds: number[],
  jobId?: number,
): {
  summary: Record<number, SceneCommentSummary>;
  refresh: () => void;
} {
  const [summary, setSummary] = useState<Record<number, SceneCommentSummary>>({});
  // sceneIds 可能每次 render 都是新数组（即使内容同），把内容序列化成 key 触发依赖
  const key = sceneIds.length > 0
    ? sceneIds.slice().sort((a, b) => a - b).join(',')
    : '';

  const load = useCallback(async () => {
    if (!key) {
      setSummary({});
      return;
    }
    try {
      const data = await getSceneCommentSummaryBatch({
        sceneIds: key.split(',').map(Number),
        jobId,
      });
      setSummary(data);
    } catch (e) {
      // 失败不阻塞主流程；列表"评论"列退化为不展示标题
      console.warn('加载评论摘要失败', e);
    }
  }, [key, jobId]);

  useEffect(() => { void load(); }, [load]);

  return { summary, refresh: load };
}
